import { useIntl } from '@/intl'
import { getOldIconForService, ServiceIcon } from '@/scopes/checkout/components/ServiceIcon'
import { useBookingDraftQuery } from '@/scopes/checkout/domain/hooks'
import { LazyImage } from '@/scopes/common/components/Image'
import * as Dialog from '@radix-ui/react-dialog'
import {
  Accordion,
  Banner,
  Button,
  Collapsible,
  SpaceBetween,
  tk,
  Typography,
  verticalGap,
  zIndex,
} from '@sunwebgroup/ui-components'
import { Icon, Rating } from '@sunwebgroup/ui-components/uds'
import { values } from 'ramda'
import * as React from 'react'
import { useState } from 'react'
import { styled } from 'styled-components'

const Wrapper = styled.div`
  display: none;
  background: ${tk('color_semantic_bg')};
  box-shadow: ${tk('shadow_lg_bottom')};
  border-radius: ${tk('radius_large')};
  ${verticalGap('spacing_xs')}
  align-self: start;
  width: 100%;
  @media ${tk('mq_desktop_md')} {
    display: initial;
  }
`
const AccommodationName = styled(Typography)`
  color: ${tk('color_semantic_text')};
`
const Location = styled(Typography)`
  color: ${tk('color_semantic_text_strong')};
  margin-top: ${tk('spacing_xs')};
`
const BasketImageWrapper = styled.div`
  height: 180px;
  width: 100%;
  border-top-right-radius: ${tk('radius_large')};
  border-top-left-radius: ${tk('radius_large')};

  > div {
    border-top-right-radius: ${tk('radius_large')};
    border-top-left-radius: ${tk('radius_large')};
  }
`
const Content = styled(SpaceBetween)`
  padding: ${tk('spacing_md')};
`
const IncludedServicesWrapper = styled(SpaceBetween)`
  ${Icon}, ${Typography} {
    color: ${tk('color_semantic_text_strong')};
  }
`
const DepartureDate = styled(Typography)`
  color: ${tk('color_semantic_text')};
`
const BookingDetails = styled(Typography)`
  color: ${tk('color_semantic_text_subdued')};
`
const GroupDetailsLabel = styled(Typography)`
  color: ${tk('color_semantic_text')};
`
const GroupDetailsName = styled(Typography)`
  color: ${tk('color_semantic_text_subdued')};
`
const BookingPriceDetails = styled(SpaceBetween)`
  flex-wrap: wrap;
  ${Typography} {
    color: ${tk('color_semantic_text')};
  }
`
const ServiceOverview = styled(Accordion.Item)`
  padding: ${tk('spacing_sm')} ${tk('spacing_none')};
  ${Typography} {
    color: ${tk('color_semantic_text')};
  }
  &:first-of-type {
    border-top: 2px solid ${tk('color_semantic_border_subdued')};
  }
  &:last-of-type {
    border-bottom: none;
  }
  ${Collapsible} {
    padding-top: ${tk('spacing_sm')};
  }
`
const ServiceOverviewBody = styled(SpaceBetween)`
  padding-left: ${tk('spacing_lg')};
`
const BasketTrigger = styled(Button)`
  background: ${tk('color_semantic_bg')};
  box-shadow: ${tk('shadow_lg_top')};
  ${verticalGap('spacing_xs')}
  align-self: start;
  min-width: 0;
  position: fixed;
  bottom: 0;
  width: 100%;
  z-index: ${zIndex.header};
  padding: ${tk('spacing_sm')} ${tk('spacing_md')};
  pointer-events: all;
  border-radius: ${tk('radius_none')};
  left: 0;

  @media ${tk('mq_desktop_md')} {
    display: none;
  }
`
const BasketTriggerContent = styled(SpaceBetween)`
  width: 100%;
`
const BasketMobileHeader = styled.div`
  position: absolute;
  top: 0;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  height: 50px;
  padding: ${tk('spacing_sm')};
  left: 0;
  ${Typography} {
    margin: 0 auto;
    color: ${tk('color_semantic_text')};
  }
`
const BasketMobileWrapper = styled(Dialog.Content)`
  background: ${tk('color_semantic_bg')};
  box-shadow: ${tk('shadow_lg_top')};
  border-radius: ${tk('radius_extra_large')};
  padding: ${tk('spacing_md')};
  width: 100%;
  z-index: ${zIndex.menuOrDrawer};
  position: fixed;
  bottom: 0;
  left: 0;
  max-height: calc(100% - ${tk('spacing_5xl')});
  min-height: 300px;
  height: 100%;
  top: auto;
  padding-bottom: ${tk('spacing_5xl')};
  padding-top: ${tk('spacing_4xl')};
`
const BasketMobileContent = styled.div`
  overflow-y: scroll;
  height: 100%;
`
const BasketOverlay = styled(Dialog.Overlay)`
  background-color: ${tk('color_semantic_bg_inverse_hover')};
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  position: fixed;
  opacity: 0.5;
  margin-top: 0 !important;
  z-index: ${zIndex.menuOrDrawer - 1};
`
const BasketImageWrapperMobile = styled.div`
  height: 120px;
  width: 50%;
`
const CloseButton = styled(Button)`
  padding: 0;
  height: auto;
  min-height: auto;
`
const TriggerButton = styled.div`
  width: ${tk('spacing_2xl')};
  display: flex;
  justify-content: center;
  align-items: center;
`
const IsRebookingBanner = styled.div`
  ${Icon} {
    display: none;
  }
`

export function Basket() {
  const intl = useIntl()
  const { isLoading, data } = useBookingDraftQuery()
  if (isLoading)
    return ''
  const basketInformation = data?.basketInformation
  const { groupInformation } = data ?? {}
  const { participants } = data ?? {}
  if (basketInformation && !basketInformation.accommodationName)
    return ''

  const startDate = basketInformation?.accommodationDates?.startDate ? new Date(basketInformation.accommodationDates.startDate) : null
  const endDate = basketInformation?.accommodationDates?.endDate ? new Date(basketInformation.accommodationDates.endDate) : null

  return (
    <>
      <Wrapper>
        <BasketImageWrapper>
          <LazyImage
            src={basketInformation?.imageUrl ?? ''}
            alt={basketInformation?.accommodationName}
            srcSet={[560, 940, 1200]}
            sizes="(min-width: 1280px) 936px, 100vw"
            loading="lazy"
          />
        </BasketImageWrapper>
        <Content direction="y" spacing={tk('spacing_md')}>
          <SpaceBetween direction="y" spacing={tk('spacing_none')}>
            <Rating rating={basketInformation?.rating || 0} />
            <AccommodationName $variant="heading-md" as="span">
              {basketInformation?.accommodationName}
            </AccommodationName>
            <Location $variant="body-sm">{basketInformation?.location}</Location>
          </SpaceBetween>
          <SpaceBetween direction="y" spacing={tk('spacing_none')}>
            <IncludedServices includedServices={basketInformation?.servicesIncluded ?? []} />
            {groupInformation && basketInformation?.accommodationDates && (
              <>
                <DepartureDate $variant="body-md-bold">
                  {startDate?.toLocaleDateString(intl.locale, { weekday: 'short', day: 'numeric', month: 'short' })}
                  {' - '}
                  {endDate?.toLocaleDateString(intl.locale, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                  {' '}
                  (
                  {basketInformation?.accommodationDates?.duration}
                  )
                </DepartureDate>
              </>
            )}
            {(!groupInformation || !basketInformation?.accommodationDates) && (
              <>
                <DepartureDate $variant="body-md-bold">{basketInformation?.departureDate}</DepartureDate>
                <BookingDetails $variant="body-sm-bold">
                  {basketInformation?.duration}
                  {' '}
                  {participants && values(participants).length > 1 && `, ${basketInformation?.occupancy}`}
                </BookingDetails>
              </>
            )}

          </SpaceBetween>
          {groupInformation && (
            <SpaceBetween direction="y" spacing={tk('spacing_none')}>
              <GroupDetailsLabel $variant="body-md-bold">
                {' '}
                {intl.formatMessage({ id: 'groups_checkout_steps_basket_group_info_label' })}
              </GroupDetailsLabel>
              <GroupDetailsName $variant="body-sm-bold">
                {' '}
                {intl.formatMessage(
                  { id: 'groups_checkout_steps_basket_group_information' },
                  { groupName: groupInformation.name, groupSize: groupInformation.numberOfParticipants },
                )}
              </GroupDetailsName>
            </SpaceBetween>
          )}
          <PriceBreakDown />
          <Accordion.Root>
            {basketInformation?.servicesOverview?.map((service, index) => (
              <ServiceOverview
                key={index}
                title={service.title}
                icon={getOldIconForService(service.serviceAName || service.icon)}
                defaultOpen={!service.isCollapsedByDefault}
                iconSize="small"
                iconIndicatorSize="small"
              >
                <ServiceOverviewBody direction="y" spacing={tk('spacing_2xs')}>
                  {service.lines.map((line, indexLine) => (
                    <Typography key={indexLine} $variant={line.isTitle ? 'body-md-bold' : 'body-md'}>
                      {line.text}
                    </Typography>
                  ))}
                </ServiceOverviewBody>
              </ServiceOverview>
            ))}
          </Accordion.Root>
        </Content>
      </Wrapper>
      <MobileBasket />
    </>
  )
}

function MobileBasket() {
  const intl = useIntl()
  const { isLoading, data } = useBookingDraftQuery()
  const [isOpen, setIsOpen] = useState(false)
  if (isLoading)
    return ''
  const basketInformation = data?.basketInformation
  if (basketInformation && !basketInformation.accommodationName)
    return ''
  const { groupInformation } = data ?? {}

  const toggle = () => {
    setIsOpen(!isOpen)
  }

  return (
    <>
      <BasketTrigger $hierarchy="tertiary" $withoutColor onClick={toggle}>
        <BasketTriggerContent direction="x" justifyContent="space-between" alignItems="center">
          <Typography $variant="body-lg-bold" as="span">
            {intl.formatMessage({ id: 'checkout_steps_basket_total_price_text' })}
          </Typography>
          <SpaceBetween direction="x" spacing={tk('spacing_xs')} alignItems="center">
            <Typography $variant="body-lg-bold" as="span">
              {basketInformation?.totalPrice}
            </Typography>
            <TriggerButton>
              {isOpen ? <Icon glyph="ChevronDown" size={24} /> : <Icon glyph="ChevronUp" size={24} />}
            </TriggerButton>
          </SpaceBetween>
        </BasketTriggerContent>
      </BasketTrigger>

      <Dialog.Root modal open={isOpen}>
        <BasketOverlay onClick={toggle} />
        <BasketMobileWrapper>
          <BasketMobileHeader>
            <Typography $variant="body-lg-bold" as="span">
              {intl.formatMessage({ id: 'checkout_steps_basket_mobile_header' })}
            </Typography>
            <CloseButton $withoutColor $hierarchy="tertiary" onClick={toggle}>
              <Icon glyph="DismissSmall" size={24} />
            </CloseButton>
          </BasketMobileHeader>
          <BasketMobileContent>
            <SpaceBetween direction="y" spacing={tk('spacing_xs')}>
              <SpaceBetween direction="x" spacing={tk('spacing_xs')}>
                <BasketImageWrapperMobile>
                  <LazyImage
                    src={basketInformation?.imageUrl ?? ''}
                    alt={basketInformation?.accommodationName}
                    srcSet={[560, 940, 1200]}
                    sizes="(min-width: 1280px) 936px, 100vw"
                    loading="lazy"
                  />
                </BasketImageWrapperMobile>

                <SpaceBetween direction="y" spacing={tk('spacing_none')}>
                  <Rating rating={basketInformation?.rating || 0} />
                  <AccommodationName $variant="heading-md" as="span">
                    {basketInformation?.accommodationName}
                  </AccommodationName>
                  <Location $variant="body-xs">{basketInformation?.location}</Location>
                </SpaceBetween>
              </SpaceBetween>
              <SpaceBetween direction="y" spacing={tk('spacing_none')}>
                <DepartureDate $variant="body-md-bold">{basketInformation?.departureDate}</DepartureDate>
                <BookingDetails $variant="body-sm-bold">
                  {basketInformation?.duration}
                  ,
                  {basketInformation?.occupancy}
                </BookingDetails>
              </SpaceBetween>
              {groupInformation && (
                <SpaceBetween direction="y" spacing={tk('spacing_none')}>
                  <GroupDetailsLabel $variant="body-md-bold">
                    {' '}
                    {intl.formatMessage({ id: 'groups_checkout_steps_basket_group_info_label' })}
                  </GroupDetailsLabel>
                  <GroupDetailsName $variant="body-sm-bold">
                    {' '}
                    {intl.formatMessage(
                      { id: 'groups_checkout_steps_basket_group_information' },
                      { groupName: groupInformation.name, groupSize: groupInformation.numberOfParticipants },
                    )}
                  </GroupDetailsName>
                </SpaceBetween>
              )}
              <PriceBreakDown />
              <Accordion.Root>
                {basketInformation?.servicesOverview?.map((service, index) => (
                  <ServiceOverview
                    key={index}
                    title={service.title}
                    icon={getOldIconForService(service.serviceAName || service.icon)}
                    defaultOpen={!service.isCollapsedByDefault}
                    iconSize="small"
                    iconIndicatorSize="small"
                  >
                    <ServiceOverviewBody direction="y" spacing={tk('spacing_2xs')}>
                      {service.lines.map((line, indexLine) => (
                        <Typography key={indexLine} $variant={line.isTitle ? 'body-md-bold' : 'body-md'}>
                          {line.text}
                        </Typography>
                      ))}
                    </ServiceOverviewBody>
                  </ServiceOverview>
                ))}
              </Accordion.Root>
            </SpaceBetween>
          </BasketMobileContent>
        </BasketMobileWrapper>
      </Dialog.Root>
    </>
  )
}

function PriceBreakDown() {
  const intl = useIntl()
  const { isLoading, data } = useBookingDraftQuery()
  if (isLoading)
    return ''
  const basketInformation = data?.basketInformation
  const { participants } = data ?? {}
  if (basketInformation && !basketInformation.accommodationName)
    return ''

  return (
    <>
      {data?.isRebooking && basketInformation?.totalPackagePrice
        ? (
            <SpaceBetween direction="y" spacing={tk('spacing_none')}>
              <Typography $variant="body-md-bold" as="span">
                {intl.formatMessage({ id: 'checkout_steps_basket_rebooking_price_breakdown' })}
              </Typography>
              <BookingPriceDetails direction="x" justifyContent="space-between">
                <Typography $variant="body-md" as="span">
                  {intl.formatMessage({ id: 'checkout_steps_basket_package_price_text' })}
                </Typography>
                <SpaceBetween direction="y" spacing={tk('spacing_2xs')}>
                  <Typography $variant="body-md-bold" as="span">
                    {basketInformation?.totalPackagePrice}
                  </Typography>
                  <Typography $variant="body-sm" as="span">
                    {basketInformation?.avgTotalPackagePrice}
                  </Typography>
                </SpaceBetween>
              </BookingPriceDetails>
              <BookingPriceDetails direction="x" justifyContent="space-between">
                <Typography $variant="body-md" as="span">
                  {intl.formatMessage({ id: 'checkout_steps_basket_previous_booking_paid_text' })}
                </Typography>
                <Typography $variant="body-md-bold" as="span">
                  {basketInformation?.originalBookingPaidAmount}
                </Typography>
              </BookingPriceDetails>
              <BookingPriceDetails direction="x" justifyContent="space-between">
                <Typography $variant="heading-md" as="span">
                  {intl.formatMessage({ id: 'checkout_steps_basket_amount_due_text' })}
                </Typography>
                <Typography $variant="heading-md" as="span">
                  {basketInformation?.totalPrice}
                </Typography>
              </BookingPriceDetails>
              {basketInformation?.rebookingWarning && (
                <IsRebookingBanner>
                  <Banner $type="success">
                    <Typography $variant="body-md" as="span">
                      {' '}
                      {basketInformation?.rebookingWarning}
                    </Typography>
                  </Banner>
                </IsRebookingBanner>
              )}
            </SpaceBetween>
          )
        : (
            <SpaceBetween direction="y" spacing={tk('spacing_none')}>
              <BookingPriceDetails direction="x" justifyContent="space-between">
                <Typography $variant="heading-md" as="span">
                  {intl.formatMessage({ id: 'checkout_steps_basket_total_price_text' })}
                </Typography>
                <Typography $variant="heading-md" as="span">
                  {basketInformation?.totalPrice}
                </Typography>
              </BookingPriceDetails>
              {participants && values(participants).length > 1 && (
                <BookingPriceDetails direction="x" justifyContent="space-between">
                  <Typography $variant="body-md" as="span">
                    {intl.formatMessage({ id: 'checkout_steps_basket_average_price_text' })}
                  </Typography>
                  <Typography $variant="body-md-bold" as="span">
                    {basketInformation?.avgPrice}
                  </Typography>
                </BookingPriceDetails>
              )}
            </SpaceBetween>
          )}
    </>
  )
}
function IncludedServices({ includedServices }: { includedServices: string[] }) {
  if (!includedServices?.length)
    return null

  return (
    <IncludedServicesWrapper direction="x" spacing={tk('spacing_2xs')}>
      {includedServices.map((service, index) => {
        return (
          <React.Fragment key={index}>
            <ServiceIcon type={service} size={24} />
            {index === includedServices.length - 1 ? '' : <Typography> + </Typography>}
          </React.Fragment>
        )
      })}
    </IncludedServicesWrapper>
  )
}
